import { isManualTask, type ManualTaskDto, setTaskField, setTaskStatus, useTasks } from "@repo/data-store";
import { EmptyState } from "@repo/ui/components/empty-state";
import { Button } from "@repo/ui/components/ui/button";
import { createFileRoute } from "@tanstack/react-router";
import { ListChecks, Plus } from "lucide-react";
import { useMemo } from "react";
import { openOverlay } from "@/app/overlays";
import { getTaskIdentifier, getTaskTitle, TaskListRow } from "@/features/tasks";
import { TASK_STATUS_LABELS } from "@/features/tasks/lib/task-status";
import { GroupedList, type GroupedListGroup } from "@/shared/common/grouped-list";
import { SearchFilterBar } from "@/shared/common/search-filter-bar";

export const Route = createFileRoute("/tasks/")({
  component: TasksComponent,
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: (search.q as string) || undefined,
  }),
});

const TASK_STATUS_ORDER = [
  "in-progress",
  "requested",
  "draft",
  "ready",
  "on-hold",
  "completed",
  "cancelled",
  "failed",
] as const;

function TasksComponent() {
  const { q: search = "" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { tasks, isLoading } = useTasks();
  const setSearch = (value: string) => navigate({ search: { q: value || undefined }, replace: true });

  const groups = useMemo<GroupedListGroup<ManualTaskDto>[]>(() => {
    const filtered = tasks.filter(isManualTask).filter((task) => {
      if (!search) return true;
      const title = getTaskTitle(task).toLowerCase();
      const owner = task.owner?.display?.toLowerCase() ?? "";
      const patient = task.for?.display?.toLowerCase() ?? "";
      const identifier = getTaskIdentifier(task)?.toLowerCase() ?? "";
      const q = search.toLowerCase();
      return title.includes(q) || owner.includes(q) || patient.includes(q) || identifier.includes(q);
    });

    return TASK_STATUS_ORDER.map((status) => ({
      id: status,
      name: TASK_STATUS_LABELS[status],
      items: filtered
        .filter((t) => t.status === status)
        .sort((a, b) => {
          const idA = getTaskIdentifier(a) ?? "";
          const idB = getTaskIdentifier(b) ?? "";
          return idB.localeCompare(idA, undefined, { numeric: true });
        }),
    }));
  }, [tasks, search]);

  return (
    <GroupedList
      groups={groups}
      isLoading={isLoading}
      hideEmptyGroups
      header={
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <SearchFilterBar
              search={search}
              onSearchChange={setSearch}
              placeholder="Search tasks..."
              statusOptions={[]}
              statusValue={null}
              onStatusChange={() => {}}
            />
          </div>
          <div className="pr-4">
            <Button size="sm" onClick={() => openOverlay("task.create", {})}>
              <Plus className="size-4 mr-1" />
              New Task
            </Button>
          </div>
        </div>
      }
      renderItem={(task) => (
        <TaskListRow
          task={task}
          onStatusChange={(taskId, status) => setTaskStatus({ taskId, status })}
          onPriorityChange={(taskId, priority) => setTaskField({ taskId, priority })}
        />
      )}
      getItemKey={(task) => task.taskId}
      emptyMessage="No tasks found"
      isFiltered={!!search}
      emptyState={
        <EmptyState className="m-auto">
          <EmptyState.Icon>
            <ListChecks className="size-4" />
          </EmptyState.Icon>
          <EmptyState.Content>
            <EmptyState.Title>No tasks yet</EmptyState.Title>
            <EmptyState.Description>Tasks will appear here as work is assigned.</EmptyState.Description>
          </EmptyState.Content>
          <EmptyState.Actions>
            <Button size="sm" onClick={() => openOverlay("task.create", {})}>
              <Plus className="size-4 mr-1" />
              New Task
            </Button>
          </EmptyState.Actions>
        </EmptyState>
      }
    />
  );
}
